import _ from "lodash";
import React from "react";

// DS
import { CopyOutlined, ArrowLeftPaneOutlined } from "@toddle-edu/ds-icons";
import { colors } from "@toddle-edu/ds-theme";

// Constant
import {
  RUBRIC,
  CHECKLIST,
  SINGLE_POINT_RUBRIC,
  WRITING,
  STREAMING,
  LOADING,
  BROWSING_THE_INTERNET,
  CURRICULUM_TYPE_PYP,
  CURRICULUM_TYPE_UBD,
  CURRICULUM_TYPE_DP,
  CURRICULUM_TYPE_MYP,
  DP_ASSESSMENT_RUBRIC,
  MYP_INTERDISCIPLINARY,
  MYP_OBJECTIVE_RUBRIC,
  MYP_INTERDISCIPLINARY_CRITERIA_RUBRIC,
  ASSESSMENT_TYPE_AI_TUTOR,
  ANALYSIS,
  SCORE_BASED_RUBRIC,
  STANDARD_BASED_RUBRIC,
  THINKING,
} from "Constants/stringConstants";
import {
  INTERDISCIPLINARY_UNIT_TYPES,
  SUBJECTS_TO_CONSIDER_AS_LANGUAGE,
} from "./Constants";

// Utils and services
import {
  generateRandomId,
  htmlToText,
  getStyleStrippedText,
  localSearch,
} from "Utils";
import ACLStore from "lib/aclStore";
import { isDateInBetween } from "Utils/DateTimeHelpers";
import {
  getFieldValue,
  getUnitPlanFieldsFromAssessment,
  getResolvedMinimalTreeSet,
  getExcludedFieldsForAI,
  getMYPObjectiveLevelFromSubject,
  getResolvedMinimalTree,
} from "Assessments/modules/Utils";
import { promiseAllObject } from "Utils/Common";
import {
  getLanguageLocalisedContent,
  getAITutorFieldsFromAIResponse,
  replaceXMLTags,
  ASSESSMENT_TOOLS_OBJ,
  AI_TUTOR_LEARNING_TOOLS,
} from "@toddle-edu/common-utils";
import {
  convertHtmlToText,
  getAssessmentLearningGoalsContext,
  getPromptLabelsObj,
} from "Utils/AIUtils";
import {
  getIsAITutorEnhancementsV2Enabled,
  getIsAITutorModalitiesEnabled,
} from "Utils/ACLStoreUtils";

// third party
import showdown from "showdown";

const styles = {
  sidebarLabelStyle: {
    whiteSpace: "pre-wrap",
  },
};

const _getCurrentModality = ({ learningTool, openingMessageResource }) => {
  if (!_.isEmpty(learningTool)) return learningTool;
  const attachmentGroups = _.get(
    openingMessageResource,
    "attachmentGroups",
    []
  );
  const isWorkbookPresent = _.find(attachmentGroups, item => {
    const attachments = _.get(item, "attachments", []);
    const workbookAttachment = _.find(attachments, { type: "WORKBOOK" });
    return !_.isEmpty(workbookAttachment);
  });

  return isWorkbookPresent ? "create_template_multipage" : "";
};

export const getOveriddenModelForWhiteboardModality = learningTool => {
  if (
    getIsAITutorModalitiesEnabled() &&
    learningTool === "create_template_multipage" &&
    ACLStore.can("FeatureFlag:EnableAdvancedModelForWhiteboard")
  ) {
    return { overriddenModel: "vertexai:gemini-3-flash" };
  }
  return {};
};

const _getSelectedFieldHasValue = params => {
  const {
    context,
    fieldList = {},
    assessmentDetails,
    selectedAssessmentField,
  } = params;

  if (!_.isEmpty(context) && !_.isEmpty(context?.[selectedAssessmentField])) {
    switch (selectedAssessmentField) {
      case "textTitles":
        return _.some(
          context?.[selectedAssessmentField],
          val => !_.isEmpty(_.trim(val?.value))
        );
      case "twoTextTitles": {
        const result = _.filter(
          context?.[selectedAssessmentField],
          val => !_.isEmpty(_.trim(val?.value))
        );

        return _.size(result) >= 2;
      }
      default:
        return !_.isEmpty(context?.[selectedAssessmentField]);
    }
  }

  const allFields = _.get(assessmentDetails, "allFields", []);
  const field = _.find(allFields, { uid: selectedAssessmentField });

  const { uid, value } = field || {};
  const { type } = fieldList[uid] || {};

  if (ASSESSMENT_TOOLS_OBJ[selectedAssessmentField]) {
    const assessmentTools = _.get(
      assessmentDetails,
      "assessmentTools.edges",
      []
    );

    const selectedAssessmentTool = _.find(assessmentTools, ({ node }) => {
      return _.get(node, "assessmentToolType") === selectedAssessmentField;
    });

    const assessmentToolItem = _.get(
      selectedAssessmentTool,
      "node.assessmentToolItem",
      {}
    );

    switch (selectedAssessmentField) {
      case RUBRIC: {
        const indicators = _.get(assessmentToolItem, "indicators", []);
        const descriptors = _.get(assessmentToolItem, "descriptors", []);

        const hasIndicators = _.some(
          indicators,
          indicator => !_.isEmpty(indicator?.label)
        );

        const hasDescriptors = _.some(
          descriptors,
          descriptor => !_.isEmpty(descriptor?.label)
        );

        return hasIndicators || hasDescriptors;
      }
      case SINGLE_POINT_RUBRIC: {
        const standards = _.get(assessmentToolItem, "standards", []);

        return _.some(
          standards,
          standard =>
            !_.isEmpty(standard?.title) && !_.isEmpty(standard?.subTitle)
        );
      }
      case CHECKLIST: {
        const checklistItems = _.get(assessmentToolItem, "checklistItems", []);
        return _.some(checklistItems, item => !_.isEmpty(item?.label));
      }
      default:
        return !_.isEmpty(assessmentToolItem);
    }
  }

  switch (type) {
    case "InputNumberList": {
      if (_.every(value, val => _.has(val, "isSelected"))) {
        const selectedValues = _.filter(value, val => val?.isSelected);
        return _.some(selectedValues, val => !_.isEmpty(_.trim(val?.value)));
      } else {
        return _.some(value, val => !_.isEmpty(_.trim(val?.value)));
      }
    }
  }

  switch (uid) {
    case "goals":
      return _.some(value, val => !_.isEmpty(val?.value));
    case "duration":
      return value !== 0;
    case "measureAssessing":
      return false;
    case "inquiryQuestions": {
      return !_.isEmpty(
        _.filter(_.flatten(_.values(value)), item => {
          if (_.has(item, "value")) {
            return !_.isEmpty(item?.value?.trim?.());
          } else {
            return !_.isEmpty(item?.trim?.());
          }
        })
      );
    }
    case "pedagogicalApproachDP": {
      if (_.every(value, key => _.has(key, "value"))) {
        return !_.isEmpty(
          _.filter(value, key => _.get(key, "isSelected", false))
        );
      }

      return !_.isEmpty(value);
    }
    case "content":
    case "disciplinaryContent": {
      if (_.isObject(value)) {
        return _.some(value, (val, key) => !_.isEmpty(_.trim(htmlToText(val))));
      }
      return !_.isEmpty(_.trim(htmlToText(value)));
    }
    default: {
      if (_.trim(htmlToText(value)) === "\n") return false;
      return !_.isEmpty(_.trim(htmlToText(value)));
    }
  }
};

export const getSelectedFieldHasValue = params => {
  const { step, stepList } = params;

  if (step) {
    const fields = _.get(stepList, `${step}.fields`, []);

    return _.some(fields, field => {
      return _getSelectedFieldHasValue({
        ...params,
        selectedAssessmentField: field,
      });
    });
  } else {
    return _getSelectedFieldHasValue(params);
  }
};

export const getBlockActions = params => {
  const {
    t,
    assessmentType,
    currentAssessmentTypes,
    assessmentToolTypes,
    assessmentFieldValues,
    isAddButtonDisabled,
    isAITutor,
  } = params;

  const actions = [
    {
      key: "copy",
      label: "common:copy",
      icon: <CopyOutlined />,
    },
  ];

  if (_.size(_.keys(assessmentFieldValues)) > 1) {
    const { localeLabel = "", label: assessmentTypeLabel = "" } =
      currentAssessmentTypes[assessmentType] || {};
    const label = localeLabel ? t(localeLabel) : assessmentTypeLabel;

    if (!isAITutor) {
      actions.unshift({
        key: "add_to_le",
        label: "classRoom:add_to_label",
        localisationParams: { label },
        icon: <ArrowLeftPaneOutlined />,
        disabled: isAddButtonDisabled,
      });
    }
  } else {
    if (assessmentToolTypes) {
      const assessmentToolKey =
        ASSESSMENT_TOOLS_OBJ[_.first(assessmentToolTypes)]?.entityType ||
        _.first(assessmentToolTypes);

      const assessmentToolObj =
        ASSESSMENT_TOOLS_OBJ?.[assessmentToolKey] ||
        ASSESSMENT_TOOLS_OBJ?.[_.first(assessmentToolTypes)];

      const { locale, label } = assessmentToolObj || {};

      if (!isAITutor) {
        actions.unshift({
          key: "add_assessment_tool",
          label: "classRoom:add_to_label",
          fieldType: _.first(assessmentToolTypes),
          localisationParams: {
            label: locale ? t(locale) : label,
          },
          icon: <ArrowLeftPaneOutlined />,
          disabled: isAddButtonDisabled,
        });
      }
    }
  }

  return actions;
};

export const getParamsForAddToLEAction = async params => {
  const { fieldList, assessmentFieldValues, assessmentToolType } = params || {};

  const assessmentFieldsObj = {};
  const promises = {};

  _.forEach(assessmentFieldValues, (fieldValue, fieldKey) => {
    const fieldType = _.get(fieldList, `${fieldKey}.type`);

    if (fieldType) {
      switch (fieldType) {
        case "InputNumberList":
        case "LearningIntentions":
          {
            // split the block text into array by newLine
            const values = _.map(_.split(fieldValue, "\n"), value =>
              value.trim().replace(/^\*\s*|\d+\.\s*/, "")
            );

            const filteredValues = _.filter(values, value => !_.isEmpty(value));

            assessmentFieldsObj[`${fieldKey}`] = _.map(
              filteredValues,
              value => {
                return {
                  id: generateRandomId(),
                  value,
                };
              }
            );
          }
          break;
        case "RichText":
          promises[`${fieldKey}`] = getTextValueForBlock({
            key: fieldKey,
            content: fieldValue,
            fieldType,
            assessmentToolType,
          });
          break;
        default:
          assessmentFieldsObj[`${fieldKey}`] = fieldValue;
          break;
      }
    }
  });

  const resolvedPromises = await promiseAllObject(promises);

  return { ...assessmentFieldsObj, ...resolvedPromises };
};

export const getIsLessonPlanningAIRevampEnabled = () => {
  return ACLStore.can("FeatureFlag:LessonPlanningAIRevamp");
};

export const getIsAITutorMissingFieldsFixEnabled = () => {
  return ACLStore.can("FeatureFlag:EnableAITutorMissingFieldsFix");
};

export const getIsSkipSemanticSearchEnabled = (
  isAITutor,
  isRevampUIEnabled
) => {
  return (
    isRevampUIEnabled ||
    (isAITutor && getIsAITutorEnhancementsV2Enabled()) ||
    (!isAITutor && ACLStore.can("FeatureFlag:SkipSemanticSearch"))
  );
};

export const getInputForAIResponse = params => {
  const {
    t,
    leId,
    selectedAction,
    gradeId,
    entityType,
    course = {},
    curriculumProgramId,
    curriculumProgramType,
    assessmentDetails,
    unitPlanFields,
    unitPlanTemplate,
    excludedFields,
    template,
    shouldProcessAttachments,
    prompts,
    imageUrls,
    audioUrls,
    aiConversationId,
    learningCourseId,
    isAITutor,
    schoolInstructions,
    userMessageResponseId,
    isRevampUIEnabled,
    toolChoice,
    toolNames,
  } = params || {};

  const { context, fieldsToExclude } = getCombinedContext(params);

  const subjectIds = getFieldValue({
    fields: assessmentDetails?.allFields,
    uid: "subjects",
  });

  const unitPlanValue = getFieldValue({
    fields: assessmentDetails?.allFields,
    uid: "unitPlan",
  });

  const unitPlanId = !_.isEmpty(unitPlanValue) ? unitPlanValue : null;

  const metadata = {
    gradeId,
    // -------- used specifically for DP---------- //
    subjectId: _.first(_.map(course?.subjects, "id")) || _.first(subjectIds),
    genericTagIds: _.map(
      _.filter(
        course?.genericTags,
        tag => _.get(tag, "type") === "SUBJECT_LEVEL"
      ),
      "id"
    ),
    // --------------------------------------- //
    ...(!_.isEmpty(imageUrls) ? { imageUrls } : {}),
    ...(!_.isEmpty(audioUrls) ? { audioUrls } : {}),
    conversationId: aiConversationId,
    learningCourseId,
    ...(getIsSkipSemanticSearchEnabled(isAITutor, isRevampUIEnabled)
      ? { skipSemanticSearch: true }
      : {}),
    assessmentId: leId,
    courseId: course?.id,
    unitPlanId,
  };

  context.schoolInstructions = schoolInstructions;

  if (isAITutor && getIsAITutorMissingFieldsFixEnabled()) {
    metadata.mandatoryFields = [
      "title",
      "description",
      "openingMessage",
      "aiRole",
      "aiRules",
      "analysisScale",
    ];
  }

  if (isRevampUIEnabled) {
    if (userMessageResponseId && shouldProcessAttachments) {
      metadata.attachmentParentId = userMessageResponseId;
      metadata.attachmentParentType = "AI_MESSAGE";
    }

    if (!isAITutor) {
      const fieldLocaleMapping = getFieldLocaleMapping({
        t,
        templateBody: template,
      });

      if (!_.isEmpty(fieldLocaleMapping)) {
        metadata.fieldLocaleMapping = fieldLocaleMapping;
      }
    }
  }

  const openingMessageResource = _.get(
    _.find(assessmentDetails?.allFields, {
      uid: "openingMessageResource",
    }),
    "resolvedMinimalTree",
    []
  );

  const modelSettings = getOveriddenModelForWhiteboardModality(
    _getCurrentModality({
      learningTool: params.learningTool,
      openingMessageResource,
    })
  );

  return {
    ...modelSettings,
    entityId: leId,
    entityType,
    context,
    metadata,
    toolChoice,
    toolNames,
    action: _.get(selectedAction, "action"),
    // fieldType: _.get(selectedAction, "fieldType"),
    curriculumProgramId,
    shouldProcessAttachments,
    excludedFields: getExcludedFieldsForAI({
      curriculumProgramType,
      unitPlanFields,
      unitPlanTemplate,
      excludedFields: [...(excludedFields || []), ...(fieldsToExclude || [])],
      assessmentDetails,
      template,
      prompts,
    }),
  };
};

export const getUserMessageMetadata = params => {
  const { selectedAction, selectedLearningTool, setSelectedLearningTool } =
    params || {};

  const { key, shouldPassLearningToolToAI } = selectedLearningTool || {};
  const { isPromptTemplate, modality } = selectedAction || {};

  const learningToolKey = isPromptTemplate ? modality : key;

  if (isPromptTemplate || shouldPassLearningToolToAI) {
    if (shouldPassLearningToolToAI) {
      setSelectedLearningTool?.(tool =>
        _.omit(tool, ["shouldPassLearningToolToAI"])
      );
    }

    if (key) {
      const selectedLearningTool = _.find(AI_TUTOR_LEARNING_TOOLS, {
        key: learningToolKey,
      });

      const nonLocalisedLabel = _.get(
        selectedLearningTool,
        "nonLocalisedLabel"
      );

      if (nonLocalisedLabel) {
        return {
          messageAppend: `Learning tool selected: ${nonLocalisedLabel}`,
        };
      }
    } else {
      return {
        messageAppend: "Learning tool selected: No learning tool",
      };
    }
  }

  return {};
};

const getCombinedContext = params => {
  const {
    assessmentDetails,
    course,
    curriculumProgramType,
    selectedAction,
    content,
    unitPlanFields,
    userInstructions,
    isAITutor,
    toolNames,
    learningTool,
  } = params;

  const fieldsToExclude = [];
  const context = {
    inputText: content,
    summary: selectedAction?.subtext,
    tutorBehaviour: selectedAction?.tutorBehaviour,
    userInstructions,
  };

  if (isAITutor) {
    // 1. teacherSelectedLearningTool context
    const selectedLearningTool =
      _.find(AI_TUTOR_LEARNING_TOOLS, { key: learningTool }) ||
      _.find(AI_TUTOR_LEARNING_TOOLS, { toolName: _.first(toolNames) });

    const nonLocalisedLabel = _.get(selectedLearningTool, "nonLocalisedLabel");

    context["teacherSelectedLearningTool"] = nonLocalisedLabel || null;

    // 2. tutorLearningGoals context
    const aiConversation = _.get(assessmentDetails, "aiConversation.edges");

    const taskCreationConversation = _.find(aiConversation, ({ node }) => {
      return node?.entitySubType === "TASK_CREATION";
    })?.node;

    context["tutorLearningGoals"] = _.get(
      taskCreationConversation,
      "metadata.tutorLearningGoals"
    );
  }

  context["grades"] =
    _.get(course?.primaryGrade, "name") ||
    _.join(_.map(course?.grades, "name"), ", ") ||
    _.join(_.map(assessmentDetails?.grades, "name"), ", ");

  const primaryGradeName = _.get(course, "primaryGrade.name");
  const primaryGradeId = _.get(course, "primaryGrade.id");

  if (primaryGradeName && primaryGradeId) {
    context["gradesWithId"] = `${primaryGradeName} (ID: ${primaryGradeId})`;
  } else {
    const courseGrades = _.get(course, "grades");
    const assessmentGrades = _.get(assessmentDetails, "grades");

    const gradesSource = !_.isEmpty(courseGrades)
      ? courseGrades
      : assessmentGrades;

    const formattedGradesWithId = _.chain(gradesSource)
      .map(g => {
        const name = _.get(g, "name");
        const id = _.get(g, "id");

        return name && id ? `${name} (ID: ${id})` : null;
      })
      .filter(Boolean)
      .join(", ")
      .value();

    context["gradesWithId"] = formattedGradesWithId || null;
  }

  // combined context of local fields like location, text titles etc.
  const localFieldsContext = getLocalStateContext(params);

  // some learning goals context is calculated at FE (i.e syllabusContentDP)
  const learningGoalsContext = getAssessmentLearningGoalsContext({
    assessmentDetails,
    curriculumProgramType,
  });

  const subjectResolvedMinimalTree = getResolvedMinimalTreeSet({
    fields: assessmentDetails?.allFields,
    uid: "subjects",
    curriculumType: curriculumProgramType,
  });

  // response language of the current course in case of DP
  if (curriculumProgramType === CURRICULUM_TYPE_DP) {
    const subjectName = _.get(subjectResolvedMinimalTree, "0.name");

    if (_.includes(SUBJECTS_TO_CONSIDER_AS_LANGUAGE, subjectName)) {
      context["responseLanguage"] = subjectName;
    } else {
      const courseResponseLanguage = _.find(course?.genericTags, {
        type: "RESPONSE_LANGUAGE",
      })?.label;

      if (!_.isEmpty(courseResponseLanguage)) {
        context["responseLanguage"] = courseResponseLanguage;
      }
    }
  }
  // pass objective level for MYP
  else if (curriculumProgramType === CURRICULUM_TYPE_MYP) {
    const subjects = getResolvedMinimalTreeSet({
      fields: assessmentDetails?.allFields,
      uid: "subjects",
      curriculumType: curriculumProgramType,
    });

    const objectiveLevelNodes = getResolvedMinimalTreeSet({
      fields: unitPlanFields,
      uid: "objectiveLevel",
      curriculumType: curriculumProgramType,
    });

    const objectiveLevels = getMYPObjectiveLevelFromSubject({
      objectiveLevelNodes,
      subjects,
      showSubject: true,
    });

    if (!_.isEmpty(objectiveLevels)) {
      context["objectiveLevel"] = _.join(objectiveLevels, ", ");
      context["UNIT.objectiveLevel"] = _.join(objectiveLevels, ", ");
    } else {
      fieldsToExclude.push("UNIT_objectiveLevel");
    }
  }
  return {
    context: { ...context, ...learningGoalsContext, ...localFieldsContext },
    fieldsToExclude,
  };
};

export const getRubricDescriptorLabel = params => {
  const { criterias, indicators, descriptors, descriptor } = params;

  return (
    _.find(descriptors, desc => {
      const criteriaLabel = _.find(criterias, {
        id: _.get(desc, "criteria.id"),
      })?.label;
      const indicatorLabel = _.find(indicators, {
        id: _.get(desc, "indicator.id"),
      })?.label;

      return (
        _.get(descriptor, "criteria.label") === criteriaLabel &&
        _.get(descriptor, "indicator.label") === indicatorLabel
      );
    })?.label || ""
  );
};

const getTitleForSinglePointRubric = (input = "") => {
  // Check for both ASCII colon (:) and fullwidth colon (：) used in CJK text
  const asciiIndex = _.indexOf(input, ":");
  const fullwidthIndex = _.indexOf(input, "：");

  let colonIndex;
  if (asciiIndex === -1) {
    colonIndex = fullwidthIndex;
  } else if (fullwidthIndex === -1) {
    colonIndex = asciiIndex;
  } else {
    colonIndex = Math.min(asciiIndex, fullwidthIndex);
  }

  if (colonIndex === -1) return "";

  return htmlToText(
    input
      .substring(0, colonIndex)
      .replace(/<\/?[^>]+(>|$)/g, "")
      .trim()
  );
};

const getSubtitleForSinglePointRubric = (input = "", title = "") => {
  return htmlToText(
    input
      .replace(/<\/?[^>]+(>|$)/g, "")
      .trim()
      .replace(title, "")
      .trim()
      .replace(/^[：:]\s*/, "")
  );
};

export const getFormattedResponse = ({
  content,
  fieldType,
  assessmentDetails,
  metadata,
}) => {
  try {
    const convertedHtml = convertMarkdownToHTML({ content });

    const tableRows = convertedHtml.match(/<tr>(.*?)<\/tr>/gs);

    switch (fieldType) {
      case CHECKLIST: {
        const checklistOptions = tableRows[0]
          .match(/<th.*?<\/th>/g)
          .slice(1)
          .map(header => {
            return {
              label: htmlToText(header.replace(/<\/?[^>]+(>|$)/g, "").trim()),
            };
          });

        const criterias = _.reduce(
          tableRows,
          (criterias, row) => {
            const cells = row.match(/<td.*?<\/td>/g);
            if (!cells) return criterias;

            const firstCell = cells[0];
            const firstCellData = firstCell
              .replace(/<\/?[^>]+(>|$)/g, "")
              .trim();

            if (
              _.isEmpty(checklistOptions) ||
              _.first(checklistOptions).label === "Options"
            ) {
              const secondCell = cells[1];
              const secondCellData = secondCell.replace(/<\/?td>/g, "");

              const splitedByCharacter = _.includes(secondCellData, "&#124;")
                ? "&#124;"
                : "|";

              const options = secondCellData
                .split(splitedByCharacter)
                .map(item => ({ label: item.trim() }));

              checklistOptions.push(...options);
            }

            criterias.push({ label: htmlToText(firstCellData) });
            return criterias;
          },
          []
        );

        return {
          criterias,
          checklistOptions,
        };
      }
      case RUBRIC: {
        const descriptorLevels = tableRows[0]
          .match(/<th.*?<\/th>/g)
          .slice(1)
          .map((header, index) => {
            return {
              label: htmlToText(header.replace(/<\/?[^>]+(>|$)/g, "").trim()),
              id: index + 1,
            };
          });

        const { criterias, descriptors } = _.reduce(
          tableRows,
          (criteriaDescriptorsObj, row, rowIdx) => {
            const cells = row.match(/<td.*?<\/td>/g);
            if (!cells) return criteriaDescriptorsObj;

            const firstCell = cells[0];
            const firstCellData = firstCell
              .replace(/<\/?[^>]+(>|$)/g, "")
              .trim();

            criteriaDescriptorsObj.criterias.push({
              label: htmlToText(firstCellData),
              id: rowIdx,
            });

            _.forEach(cells.slice(1), (cell, index) => {
              const cellData = cell.replace(/<\/?[^>]+(>|$)/g, "").trim();

              criteriaDescriptorsObj.descriptors.push({
                label: htmlToText(cellData),
                descriptorLevelId: index + 1,
                criteriaId: rowIdx,
              });
            });

            return criteriaDescriptorsObj;
          },
          {
            criterias: [],
            descriptors: [],
          }
        );

        return {
          criterias,
          descriptorLevels,
          descriptors,
        };
      }
      case SINGLE_POINT_RUBRIC: {
        const criterias = tableRows[0]
          .match(/<th.*?<\/th>/g)
          .map((header, index) => {
            const title = getTitleForSinglePointRubric(header);
            const subTitle = getSubtitleForSinglePointRubric(header, title);
            return {
              title,
              subTitle,
              isCriteriaStandardColumn: index === 1,
              __typename: "SinglePointRubricCriteria",
            };
          });

        const standards = tableRows.slice(1).map(row => {
          const cells = row.match(/<td.*?<\/td>/g);
          const cell = cells[1];
          const title = getTitleForSinglePointRubric(cell);
          const subTitle = getSubtitleForSinglePointRubric(cell, title);
          return { title, subTitle, __typename: "SinglePointRubricStandard" };
        });

        return {
          criterias,
          standards,
        };
      }
      case MYP_OBJECTIVE_RUBRIC:
      case MYP_INTERDISCIPLINARY_CRITERIA_RUBRIC: {
        const parser = new DOMParser();
        const doc = parser.parseFromString(convertedHtml, "text/html");

        const tableData = getTableDataForMYPRubric({
          doc,
          assessmentDetails,
          type: fieldType,
          metadata,
        });

        return _.map(tableData, ({ headers, rows, subject }) => {
          return _.map(rows, row => {
            return _.map(row, (cell, index) => {
              return {
                achievementLevel: headers[index],
                label: cell,
                subject,
              };
            });
          });
        });
      }

      case ANALYSIS: {
        const firstColumnData = [],
          secondColumnData = [];

        // Loop through each row and extract the first and second columns
        if (tableRows) {
          tableRows.forEach(row => {
            const cells = row.match(/<td>(.*?)<\/td>/gs);
            if (cells) {
              const firstColumnText = cells[0]
                .replace(/<td>|<\/td>/g, "")
                .trim();
              const secondColumnText = cells[1]
                .replace(/<td>|<\/td>/g, "")
                .trim();

              firstColumnData.push(convertHtmlToText(firstColumnText));
              secondColumnData.push(secondColumnText);
            }
          });
        }

        return { firstColumnData, secondColumnData };
      }
      case SCORE_BASED_RUBRIC:
      case STANDARD_BASED_RUBRIC: {
        const criterias = [];

        let rowsToProcess = tableRows;

        // If content is an array (direct HTML rows), use it as is
        if (_.isArray(content)) {
          rowsToProcess = content;
        }

        if (!_.isEmpty(rowsToProcess)) {
          // Extract criteria from data rows (skip header row if it exists)
          const dataRows = _.filter(rowsToProcess, row => {
            return _.includes(row, "<td") && !_.includes(row, "<th");
          });

          _.forEach(dataRows, row => {
            const cells = row.match(/<td.*?<\/td>/gs);

            if (!_.isEmpty(cells)) {
              // Extract criteria from first cell
              const criteriaCell = _.first(cells);

              // Use class/id to extract values - updated to handle span elements specifically
              const getLabelFromElement = element => {
                const labelMatch = element.match(
                  /<span[^>]*class\s*=\s*["']?sbr-label["']?[^>]*>(.*?)<\/span>/i
                );
                return labelMatch ? htmlToText(labelMatch[1]) : null;
              };

              const getDescriptionFromElement = element => {
                const descriptionMatch = element.match(
                  /<span[^>]*class\s*=\s*["']?sbr-description["']?[^>]*>(.*?)<\/span>/i
                );
                return descriptionMatch
                  ? htmlToText(descriptionMatch[1])
                  : null;
              };

              const getScoreFromElement = element => {
                const scoreMatch = element.match(
                  /<span[^>]*class\s*=\s*["']?sbr-score["']?[^>]*>(.*?)<\/span>/i
                );
                if (scoreMatch) {
                  const scoreText = htmlToText(scoreMatch[1]);
                  const numMatch = scoreText.match(/\d+/);
                  return numMatch ? parseInt(numMatch[0], 10) : null;
                }
                return null;
              };

              // Extract criteria information
              const criteriaLabel = getLabelFromElement(criteriaCell);
              const criteriaDescription = getDescriptionFromElement(
                criteriaCell
              );
              const maxScore = getScoreFromElement(criteriaCell);

              if (criteriaLabel) {
                const ratings = [];

                // Extract ratings from remaining cells
                _.forEach(_.slice(cells, 1), cell => {
                  const ratingLabel = getLabelFromElement(cell);
                  const ratingDescription = getDescriptionFromElement(cell);
                  const score = getScoreFromElement(cell);

                  if (ratingLabel) {
                    const rating = {
                      label: ratingLabel,
                    };

                    // Add description only if it exists
                    if (ratingDescription) {
                      rating.description = ratingDescription;
                    }

                    // Add score only if it exists
                    if (!_.isNull(score)) {
                      rating.score = score;
                    }

                    ratings.push(rating);
                  }
                });

                const criteria = {
                  label: criteriaLabel,
                  ratings,
                };

                // Add description only if it exists
                if (criteriaDescription) {
                  criteria.description = criteriaDescription;
                }

                // Add maxScore only if it exists
                if (!_.isNull(maxScore)) {
                  criteria.maxScore = maxScore;
                }

                criterias.push(criteria);
              }
            }
          });
        }

        return criterias;
      }
      default:
        return {};
    }
  } catch (e) {
    console.error(e);
    return {};
  }
};

export const getCombinedPrompts = params => {
  const {
    t,
    language,
    assessmentDetails,
    unitPlanDetails,
    curriculumProgramType,
  } = params;

  const isEnglishLanguageSelected = language === "en";

  const { flattenedPrompts } = getPromptsForSuggestionLibrary(params);

  let result = [];

  const rootPrompts = _.filter(flattenedPrompts, ({ parent } = {}) => !parent);

  let rootPromptsChildren = _.flatMap(rootPrompts, rootPrompt => {
    const { depth, children } = rootPrompt || {};

    // handles improve prompts in chat empty state
    if (depth === 2) return rootPrompt;
    else {
      return _.filter(flattenedPrompts, ({ id }) => _.includes(children, id));
    }
  });

  rootPromptsChildren = getUpdatedPrompts({
    t,
    value: rootPromptsChildren,
    assessmentDetails,
    unitPlanDetails,
    isEnglishLanguageSelected,
    curriculumProgramType,
    promptTypes: ["display", "user"],
  });

  const isMYPOrDP = _.includes(
    [CURRICULUM_TYPE_MYP, CURRICULUM_TYPE_DP],
    curriculumProgramType
  );

  if (isMYPOrDP) {
    result = _.reduce(
      rootPromptsChildren,
      (acc, prompt) => {
        const { id, label, displaySequence = 0, children: childrenPromptIds } =
          prompt || {};

        const childrenPrompts = _.reduce(
          childrenPromptIds,
          (acc, id) => {
            const prompt = _.find(flattenedPrompts, { id });
            if (prompt) acc.push(prompt);
            return _.uniqBy(acc, "id");
          },
          []
        );

        const options = [..._.slice(childrenPrompts, 0, 2)];

        if (_.size(childrenPrompts) > 2) {
          options.push({
            id: "MORE",
            label: t("common:lowercase", {
              text: `${t("common:more")} +`,
            }),
          });
        }

        if (label && _.size(options) > 0) {
          acc.push({
            id,
            options,
            label,
            displaySequence,
          });
        }

        return acc;
      },
      []
    );
  } else {
    result = _.reduce(
      rootPrompts,
      (acc, prompt) => {
        const {
          label,
          displaySequence = 0,
          children: depthTwoDisplayPromptIds,
        } = prompt || {};

        const depthOneDisplayPrompts = _.reduce(
          depthTwoDisplayPromptIds,
          (acc, id) => {
            const depthOneDisplayPromptsIds = _.get(
              _.find(flattenedPrompts, { id }),
              "children",
              []
            );

            if (!_.isEmpty(depthOneDisplayPromptsIds)) {
              _.forEach(depthOneDisplayPromptsIds, id => {
                const prompt = _.find(flattenedPrompts, { id });
                if (prompt) acc.push(prompt);
              });
            } else {
              // handles improve prompts in chat empty state
              _.forEach(depthTwoDisplayPromptIds, id => {
                const prompt = _.find(flattenedPrompts, { id });
                if (prompt) acc.push(prompt);
              });
            }

            return _.uniqBy(acc, "id");
          },
          []
        );

        const options = [..._.slice(depthOneDisplayPrompts, 0, 2)];

        if (_.size(depthOneDisplayPrompts) > 2) {
          options.push({
            id: "MORE",
            label: t("common:lowercase", {
              text: `${t("common:more")} +`,
            }),
          });
        }

        if (label && _.size(options) > 0) {
          acc.push({
            id: _.first(depthTwoDisplayPromptIds),
            options,
            label,
            displaySequence,
          });
        }

        return acc;
      },
      []
    );
  }

  return _.sortBy(result, "displaySequence");
};

export const getIsAIResponseLoading = state => {
  return [
    WRITING,
    STREAMING,
    LOADING,
    BROWSING_THE_INTERNET,
    THINKING,
  ].includes(state);
};

export const getAssessmentFieldValues = params => {
  const {
    inputString,
    assessmentToolTypes,
    shouldFilterEmptyValues = false,
    language,
    isAITutor,
  } = params;

  let assessmentFieldValues = {};

  if (isAITutor) {
    const { fields } = getAITutorFieldsFromAIResponse({
      content: inputString,
      language,
    });

    const result = _.reduce(
      fields,
      (acc, field = {}) => {
        acc[field.uid] = field.value;
        return acc;
      },
      {}
    );

    return result;
  } else {
    const {
      title: titleLabel,
      description: descriptionLabel,
      learningIntentions: learningIntentionsLabel,
    } = getLanguageLocalisedContent(language);

    const titleRegex = new RegExp(
      `\\*\\*${titleLabel}:?\\*\\*\\s*:?\\s*(.+?)(?:\n\n|\n|$)`
    );

    const goalsRegex = new RegExp(
      `\\*\\*${learningIntentionsLabel}:?\\*\\*\\s*:?\\s*\n*([\\s\\S]*?)(?=\n\n\\*\\*|$)`
    );

    let descriptionRegex = new RegExp(
      `\\*\\*${descriptionLabel}:?\\*\\*\\s*:?\\s*([\\s\\S]*)`
    );

    if (_.includes(assessmentToolTypes, DP_ASSESSMENT_RUBRIC)) {
      descriptionRegex = new RegExp(
        `\\*\\*${descriptionLabel}\\*\\*:\\s*([\\s\\S]*?)\\n\\n\\*\\*Rubric\\*\\*`
      );
    }

    const titleMatch = inputString.match(titleRegex);
    const goalsMatch = inputString.match(goalsRegex);
    const descriptionMatch = inputString.match(descriptionRegex);

    const title = titleMatch ? titleMatch[1].trim() : "";

    const goals = goalsMatch ? goalsMatch[1].trim() : "";

    let description = descriptionMatch ? descriptionMatch[1].trim() : "";
    description = _.first(_.split(description, "###"))?.trim();

    if (_.isEmpty(description)) {
      const match = !_.isEmpty(goals)
        ? goalsMatch
        : !_.isEmpty(title)
        ? titleMatch
        : null;

      if (match) {
        const remainingText = inputString?.substring(
          _.get(match, "index") + _.size(_.first(match))
        );

        description = _.first(_.split(remainingText, "###"))?.trim();
      }
    }

    assessmentFieldValues = {
      title,
      goals,
      description,
    };
  }

  if (shouldFilterEmptyValues) {
    return _.pickBy(assessmentFieldValues, value => !_.isEmpty(value));
  }

  return assessmentFieldValues;
};

export const getFormattedAssessmentFields = async params => {
  const { captureAndUploadImage, assessmentToolType, fieldList } = params;

  const assessmentFieldValues = getAssessmentFieldValues(params);

  const result = _.reduce(
    assessmentFieldValues,
    (assessmentFieldsObj, value, key) => {
      if (value) {
        assessmentFieldsObj[key] = getTextValueForBlock({
          key,
          content: value,
          fieldType: fieldList
            ? _.get(fieldList, `${key}.type`)
            : key === "description"
            ? "RichText"
            : null,
          captureAndUploadImage,
          assessmentToolType,
        });
      }
      return assessmentFieldsObj;
    },
    {}
  );

  return await promiseAllObject(result);
};

export const convertMarkdownToHTML = ({
  content,
  shouldParseContent = false,
}) => {
  const converter = new showdown.Converter({
    tables: true,
    ghCodeBlocks: true,
    // disableForced4SpacesIndentedSublists is used to disable the 4 spaces indentation for lists
    // setting this to true allows us to use 2 spaces indentation for lists
    disableForced4SpacesIndentedSublists: true,
  });

  if (shouldParseContent) {
    const parsedContent = _.replace(content, /###/g, "@@@")?.trim();
    const covertedText = converter.makeHtml(parsedContent);

    return _.replace(covertedText, /@@@/g, "###");
  }

  return converter.makeHtml(content);
};

const applyStylesToTable = tableElement => {
  if (!tableElement) return;

  tableElement.style.fontSize = "1.6rem";

  const thead = tableElement.querySelector("thead");
  thead.style.backgroundColor = colors.decorativeSurfaceTwoL;
  thead.style.color = colors.textDefault;
  thead.style.minHeight = "32px";

  const thCells = tableElement.querySelectorAll("th");
  thCells.forEach(th => {
    th.style.padding = "8px";
    th.style.textAlign = "left";
    th.style.overflow = "hidden";
    th.style.border = `1px solid ${colors.borderSubtle}`;
    th.style.fontSize = "1.2rem";
  });

  const trCells = tableElement.querySelectorAll("tr td:first-child");
  trCells.forEach(td => {
    td.style.backgroundColor = colors.decorativeSurfaceTwoL;
  });

  const tbody = tableElement.querySelector("tbody");
  tbody.style.backgroundColor = colors.surfaceWhite;
  tbody.style.color = colors.textDefault;

  const tdCells = tableElement.querySelectorAll("td");
  tdCells.forEach(td => {
    td.style.padding = "8px";
    td.style.overflow = "hidden";
    td.style.border = `1px solid ${colors.borderSubtle}`;
    td.style.fontSize = "1.2rem";
  });

  return tableElement;
};

export const replaceTablesWithLinks = async params => {
  const { content, captureAndUploadImage, assessmentToolType } = params;

  if (!captureAndUploadImage || assessmentToolType) return content;

  const convertedText = convertMarkdownToHTML({
    content,
    shouldParseContent: true,
  })?.trim();

  const tempElement = document.createElement("div");
  tempElement.innerHTML = convertedText;

  document.body.appendChild(tempElement);

  // Get all tables in the temporary element
  const tables = tempElement.getElementsByTagName("table");

  // Replace each table with the plain text "image link"
  for (let i = tables.length - 1; i >= 0; i--) {
    const table = applyStylesToTable(tables[i]);
    const url = await captureAndUploadImage(table);

    if (url) {
      const imgTag = document.createElement("img");
      imgTag.src = url;
      imgTag.alt = url;
      imgTag.class = "blobImg";
      tables[i].parentNode.replaceChild(imgTag, tables[i]);
    }
  }

  const modifiedHtmlString = tempElement.innerHTML;

  // Remove the tempElement from the document
  document.body.removeChild(tempElement);

  return modifiedHtmlString;
};

export const getTextValueForBlock = async params => {
  const {
    key,
    content,
    fieldType,
    captureAndUploadImage,
    assessmentToolType,
  } = params;

  let convertedText = convertMarkdownToHTML({
    content,
    shouldParseContent: true,
  })?.trim();

  if (fieldType === "RichText") {
    if (captureAndUploadImage) {
      convertedText = await replaceTablesWithLinks({
        content,
        captureAndUploadImage,
        assessmentToolType,
      });
    }

    if (key === "openingMessage") {
      convertedText = content;
    } else {
      convertedText = getStyleStrippedText({
        text: convertedText,
        type: "RICH_TEXT_EDITOR",
      });
    }

    // Adding 'tde-paragraph' class to <p> tags to remove default top/bottom margins
    // When content is dynamically added to Rich Text Editor (unlike user typing),
    // we need to manually add this class that RTE would normally apply automatically
    convertedText = _.replace(
      convertedText,
      /<p>/g,
      '<p class="tde-paragraph">'
    );
  } else {
    convertedText = htmlToText(convertedText, {
      wordwrap: null,
    })?.trim();
  }

  const hasDelimeter = _.includes(convertedText, "###");

  if (hasDelimeter) {
    const splitConvertedText = _.split(convertedText, "###");

    if (_.size(splitConvertedText) === 3)
      return _.trim(_.nth(splitConvertedText, 1));
  }

  return _.replace(convertedText, /###/g, "\n");
};

export const getHTMLContentForBlock = params => {
  const { content } = params;

  let convertedText = convertMarkdownToHTML({
    content,
    shouldParseContent: true,
  })?.trim();

  const tableStyle = ' style="background-color: #f5f5ff; text-align: left;"';
  convertedText = _.replace(convertedText, /<th>/g, `<th${tableStyle}>`);
  convertedText = _.replace(
    convertedText,
    /<tr>(\s*?)<td>/g,
    `<tr>$1<td${tableStyle}>`
  );

  const hasDelimeter = _.includes(convertedText, "###");

  if (hasDelimeter) {
    const splitConvertedText = _.split(convertedText, "###");

    if (_.size(splitConvertedText) === 3)
      return _.trim(_.nth(splitConvertedText, 1));
  }

  return _.replace(convertedText, /###/g, "\n");
};

export const getIsListTitleBold = string => {
  const htmlString = convertMarkdownToHTML({ content: string });
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString, "text/html");

  const paragraphs = doc.getElementsByTagName("p");

  for (let i = 0; i < paragraphs?.length; i++) {
    const paragraph = paragraphs?.[i];
    const directChildren = paragraph?.children;

    for (let j = 0; j < directChildren?.length; j++) {
      const child = directChildren?.[j];
      if (child?.tagName?.toLowerCase() === "strong") {
        return true;
      }
    }
  }

  return false;
};

const checkIsSinglePointRubric = table => {
  const rows = table.querySelectorAll("tr");

  for (let i = 1; i < rows.length; i++) {
    const cells = rows[i].querySelectorAll("td");
    if (_.size(cells) !== 3) {
      return false;
    }

    // Check if first and last cells are empty
    const firstCellIsEmpty = cells[0].innerText.trim() === "";
    const lastCellIsEmpty = cells[cells.length - 1].innerText.trim() === "";

    if (!firstCellIsEmpty || !lastCellIsEmpty) {
      return false;
    }
  }

  return true;
};

const checkIsChecklist = table => {
  // Get the column headers
  const thElements = table.querySelectorAll("th");
  const columnHeaders = [...thElements].map(th => {
    if (th.innerText.trim()) {
      return th.innerText.trim();
    }
  });
  const numColumns = columnHeaders.length;

  // Get the data in each column
  const columns = [];
  for (let i = 0; i < numColumns; i++) {
    columns.push([]);
  }

  const tdElements = table.querySelectorAll("td");
  tdElements.forEach((td, index) => {
    const columnIdx = index % numColumns;
    if (td.innerText.trim()) {
      columns[columnIdx].push(td.innerText.trim());
    }
  });

  if (_.every(_.tail(columns), column => _.isEmpty(column))) return true;

  if (_.some(columns, column => _.isEmpty(column))) return false;

  // Check if all columns (except the first column) have the same data
  const areColumnsSame = columns.slice(1).every((column, index) => {
    const columnHeader = columnHeaders[index + 1]; // +1 to skip the first column header
    return column.every(value => value === columnHeader);
  });

  return areColumnsSame;
};

const checkIsRubric = ({ language, table }) => {
  // Check if the first cell contains "Criteria"

  const headers = table.querySelectorAll("th");

  if (_.some(headers, header => _.isEmpty(header?.innerText?.trim())))
    return false;

  const criteriaLabel = _.get(
    getLanguageLocalisedContent(language),
    "criteria"
  );

  // Check if the first row's first cell contains the text "Criteria"
  const firstCellFirstRow = headers[0];
  if (
    !firstCellFirstRow ||
    firstCellFirstRow.innerText.trim() !== criteriaLabel
  ) {
    return false;
  }

  return true;
};

export const checkIsDPAssessmentRubric = params => {
  const { metadata } = params || {};
  const { metadata: { sourceDpRubricId, sourceDpRubricObjectiveIds } = {} } =
    metadata || {};

  return !_.isEmpty(sourceDpRubricId) && !_.isEmpty(sourceDpRubricObjectiveIds);
};

export const checkIsMYPRubricHeader = headers => {
  // first cell of header should be empty
  if (!_.isEmpty(_.first(headers))) return false;

  // 0, 1-2, 3-4, 5-6, 7-8 -> sum of numbers should be 36
  const extractedNumbers = _.map(headers, header => {
    const numbers = header.match(/\d+/g);
    return numbers || [];
  });

  const flattenedNumbers = _.flatten(extractedNumbers);

  return _.sumBy(flattenedNumbers, num => parseInt(num, 10)) === 36;
};

export const checkIsMYPRubric = params => {
  const { doc, assessmentDetails, type, metadata } = params;

  const tableData = getTableDataForMYPRubric({
    doc,
    assessmentDetails,
    type,
    metadata,
  });

  const nodes = getAssessmentAndUnitPlanCombinedNodes({
    assessmentDetails,
    type,
  });

  // first column of myp rubric
  const depthThreeNodes = _.filter(nodes, node => node?.depth === 3);

  if (_.isEmpty(tableData)) return false;

  // match first column of myp rubric with first column of AI generated rubric
  return _.every(tableData, ({ headers, rows, subject }) => {
    if (checkIsMYPRubricHeader(headers)) {
      return _.every(rows, row => {
        return _.some(depthThreeNodes, node => {
          const { parent, label } = node || {};

          const depthTwoNodeLabel = _.get(
            _.find(nodes, { id: parent, depth: 2 }),
            "label"
          );

          return compareMYPCriteriaNodeText({
            originalCriteria: label,
            aiGeneratedCriteria: _.first(row),
            metadata,
            subject,
            depthTwoNodeLabel,
          });
        });
      });
    }

    return false;
  });
};

export const checkIsAIAnalysisScale = params => {
  const { table, assessmentDetails } = params;

  const assessmentType = getFieldValue({
    fields: assessmentDetails?.allFields,
    uid: "assessmentType",
  });

  if (assessmentType !== ASSESSMENT_TYPE_AI_TUTOR) return false;

  const rows = table.querySelectorAll("tr");

  // remove empty rows
  const filteredRows = Array.from(rows).filter(row => {
    const cells = row.querySelectorAll("td");
    return Array.from(cells).some(cell => cell.textContent.trim() !== "");
  });

  // check for 4 rows and 2 columns
  if (_.size(filteredRows) === 4) {
    return Array.from(filteredRows).every(row => {
      const cells = row.querySelectorAll("td");
      return cells.length === 2;
    });
  }

  return false;
};

export const checkIsScoreBasedRubric = ({ language, table }) => {
  try {
    if (!table) return false;

    const thead = table.querySelector("thead");
    if (!thead) return false;

    const headerRow = thead.querySelector("tr");
    if (!headerRow) return false;

    const headerCells = headerRow.querySelectorAll("th");
    if (headerCells.length !== 2) return false;

    // Extract text content from header cells and normalize
    const firstHeaderText = _.trim(_.get(headerCells[0], "textContent", ""));
    const secondHeaderText = _.trim(_.get(headerCells[1], "textContent", ""));

    // Check if headers match "Criteria" and "Ratings"
    const hasCriteriaHeader =
      firstHeaderText ===
      _.get(getLanguageLocalisedContent(language), "criteria", "Criteria");

    const hasRatingsHeader =
      secondHeaderText ===
      _.get(getLanguageLocalisedContent(language), "ratings", "Ratings");

    return hasCriteriaHeader && hasRatingsHeader;
  } catch (error) {
    return false;
  }
};

export const checkIsStandardBasedRubric = ({ metadata }) => {
  const { metadata: { elementIds, gradeScaleId, elementType } = {} } =
    metadata || {};

  return (
    !_.isEmpty(elementIds) &&
    !_.isEmpty(gradeScaleId) &&
    !_.isEmpty(elementType)
  );
};

export const getToolTypeFromContent = params => {
  const { language, content, assessmentDetails, metadata } = params;

  const assessmentTools = [];
  const contentHTML = convertMarkdownToHTML({ content });

  const parser = new DOMParser();
  const doc = parser.parseFromString(contentHTML, "text/html");

  const table = doc.querySelector("table");

  // tools based on table
  if (table) {
    if (checkIsStandardBasedRubric({ metadata }))
      return [STANDARD_BASED_RUBRIC];
    if (checkIsScoreBasedRubric({ language, table }))
      return [SCORE_BASED_RUBRIC];
    if (checkIsAIAnalysisScale({ table, assessmentDetails })) return [ANALYSIS];
    if (checkIsChecklist(table)) return [CHECKLIST];
    if (checkIsSinglePointRubric(table)) return [SINGLE_POINT_RUBRIC];
    if (checkIsRubric({ language, table })) return [RUBRIC];
    if (
      checkIsMYPRubric({
        doc,
        assessmentDetails,
        metadata,
        type: MYP_OBJECTIVE_RUBRIC,
      })
    ) {
      assessmentTools.push(MYP_OBJECTIVE_RUBRIC);
    }
    if (
      checkIsMYPRubric({
        doc,
        assessmentDetails,
        metadata,
        type: MYP_INTERDISCIPLINARY_CRITERIA_RUBRIC,
      })
    ) {
      assessmentTools.push(MYP_INTERDISCIPLINARY_CRITERIA_RUBRIC);
    }
  }

  // tools based on metadata
  if (metadata) {
    if (checkIsDPAssessmentRubric({ metadata })) {
      return [DP_ASSESSMENT_RUBRIC];
    }
  }

  return !_.isEmpty(assessmentTools) ? assessmentTools : null;
};

const getLocalStateContext = params => {
  const { context } = params;

  const result = {};

  _.forEach(context, (value, key) => {
    if (_.isEmpty(value) && !_.isBoolean(value)) return;

    if (key === "unitPlan") {
      result[key] = _.get(value, "title.value") || "Untitled";
    } else if (_.isArray(value)) {
      if (_.every(value, item => _.has(item, "value"))) {
        result[key] = _.join(
          _.map(value, (item, index) => {
            return `${index + 1}. ${item?.value}.`;
          }),
          "\n"
        );
      } else {
        result[key] = _.join(value, ", ");
      }
    } else {
      result[key] = value;
    }
  });

  return result;
};

const getPromptSequence = item => {
  const { fieldType, isNewTagVisible } = item || {};

  const [group, subGroup] = _.split(fieldType, "_") || [];

  const isSubjectSpecificPrompt = group === "SUBJECT";
  const isMYPIDUPrompt = subGroup === MYP_INTERDISCIPLINARY;

  if (isNewTagVisible && isMYPIDUPrompt) {
    return 0;
  } else if (isNewTagVisible || isMYPIDUPrompt) {
    return 1;
  } else if (isSubjectSpecificPrompt) {
    return 2;
  } else {
    return 3;
  }
};

export const getUpdatedPrompts = params => {
  const { t, value, isEnglishLanguageSelected, sourceFrom } = params;

  const updatedPrompts = _.chain(_.orderBy(value, "fieldType"))
    .map(item => {
      const {
        isTimeDependent = false,
        startDate,
        endDate,
        displayText,
        displayTextLocale,
      } = item || {};

      const label =
        isEnglishLanguageSelected || _.isNil(displayTextLocale)
          ? displayText
          : t(displayTextLocale);

      let isNewTagVisible = false;

      if (isTimeDependent && !_.isNil(startDate) && !_.isNil(endDate)) {
        isNewTagVisible = isDateInBetween({
          date1: startDate,
          date2: endDate,
        });
      }

      return {
        ...item,
        key: item?.id,
        label,
        isNewTagVisible,
      };
    })
    .filter(item => filterPrompts({ item, ...params }))
    .sortBy(item => {
      const { fieldType, displaySequence } = item || {};

      return [getPromptSequence(item), fieldType, displaySequence];
    })
    .value();

  if (sourceFrom === "CHAT") {
    return renderUniqueSubjectPrompts({ ...params, prompts: updatedPrompts });
  }

  return updatedPrompts;
};

const renderUniqueSubjectPrompts = params => {
  const { prompts } = params;

  // this will group the prompts on the basis of their subject
  // like Visual Arts: [prompt1, prompt2] etc.
  const groupedPrompts = _.groupBy(prompts, ({ displayText } = {}) =>
    _.first(_.split(displayText, ": "))
  );

  // find the group with max number of prompts
  let maxLength = 0;
  _.forEach(groupedPrompts, group => {
    if (_.size(group) > maxLength) {
      maxLength = _.size(group);
    }
  });

  // push prompts from each group to the array
  const result = [];
  for (let i = 0; i < maxLength; i++) {
    _.forEach(groupedPrompts, group => {
      if (group[i]) {
        result.push(group[i]);
      }
    });
  }

  return result;
};

export const getAssessmentAndUnitPlanCombinedNodes = params => {
  const { assessmentDetails, type } = params;

  // assessment objective nodes
  const allFields = _.get(assessmentDetails, "allFields", []);

  const objectives = _.find(allFields, {
    uid: ASSESSMENT_TOOLS_OBJ[type]?.objectiveFieldKey,
  });

  const objectivesNodes = _.get(objectives, "resolvedMinimalTree.nodes", []);

  // unit plan objective nodes
  const unitPlanAllFields = getUnitPlanFieldsFromAssessment({
    assessmentDetails,
  });

  const unitObjectives = _.find(unitPlanAllFields, {
    uid: ASSESSMENT_TOOLS_OBJ[type]?.objectiveFieldKey,
  });

  const unitObjectivesNodes = _.get(
    unitObjectives,
    "resolvedMinimalTree.nodes",
    []
  );

  // // combine both assessment and unit plan objective nodes
  // return _.uniqBy(
  //   [...(objectivesNodes || []), ...(unitObjectivesNodes || [])],
  //   "id"
  // );

  return objectivesNodes;
};

export const getMYPObjectivesFromContent = params => {
  const { type, content, assessmentDetails, metadata } = params;

  const convertedHtml = convertMarkdownToHTML({ content });

  const parser = new DOMParser();
  const doc = parser.parseFromString(convertedHtml, "text/html");

  const tableData = getTableDataForMYPRubric({
    doc,
    assessmentDetails,
    type,
    metadata,
  });

  const nodes = getAssessmentAndUnitPlanCombinedNodes({
    assessmentDetails,
    type,
  });

  // first column of myp rubric
  const depthThreeNodes = _.filter(nodes, node => node?.depth === 3);

  const result = [];

  _.forEach(tableData, ({ headers, rows, subject }) => {
    if (checkIsMYPRubricHeader(headers)) {
      _.forEach(rows, row => {
        const node = _.find(depthThreeNodes, node => {
          const { id, label, associatedParents, parent } = node || {};
          const subjects = _.map(associatedParents, "name");

          const depthTwoNodeLabel = _.get(
            _.find(nodes, { id: parent, depth: 2 }),
            "label"
          );

          if (!_.isEmpty(subjects) && !_.isEmpty(subject)) {
            return (
              !_.includes(result, id) &&
              compareMYPCriteriaNodeText({
                originalCriteria: label,
                aiGeneratedCriteria: _.first(row),
                metadata,
                subject,
                depthTwoNodeLabel,
              }) &&
              _.includes(subjects, subject)
            );
          } else {
            return (
              !_.includes(result, id) &&
              compareMYPCriteriaNodeText({
                originalCriteria: label,
                aiGeneratedCriteria: _.first(row),
                metadata,
                subject,
                depthTwoNodeLabel,
              })
            );
          }
        });

        if (node) {
          result.push(node?.id);
        }
      });
    }
  });

  return result;
};

export const getMYPObjectivesData = params => {
  const { field, content, assessmentDetails, metadata } = params;

  const allFields = _.get(assessmentDetails, "allFields", []);

  const objectives = _.find(allFields, {
    uid: ASSESSMENT_TOOLS_OBJ[field]?.objectiveFieldKey,
  });

  const nodes = _.get(objectives, "resolvedMinimalTree.nodes", []);

  const objectiveIds = _.map(
    _.filter(nodes, node => node?.depth === 3),
    "id"
  );

  const subjects = getAssessmentAndUnitSubjectsCombined(assessmentDetails);

  const objectiveIdsFromContent = getMYPObjectivesFromContent({
    content,
    assessmentDetails,
    type: field,
    subjects,
    metadata,
  });

  return {
    objectiveIds: objectiveIdsFromContent,
    isMismatch:
      !_.isEmpty(objectiveIds) &&
      !_.isEmpty(objectiveIdsFromContent) &&
      !_.isEqual(_.sortBy(objectiveIds), _.sortBy(objectiveIdsFromContent)),
    updateObjectives:
      !_.isEmpty(objectiveIdsFromContent) &&
      !_.isEqual(_.sortBy(objectiveIds), _.sortBy(objectiveIdsFromContent)),
  };
};

export const getIsMYPRubric = type => {
  return _.includes(
    [MYP_OBJECTIVE_RUBRIC, MYP_INTERDISCIPLINARY_CRITERIA_RUBRIC],
    type
  );
};

export const keepTextAndNumbers = string => {
  return _.lowerCase(string?.replace(/[^\p{L}\p{N}\s]/gu, ""));
};

export const getAssessmentAndUnitSubjectsCombined = assessmentDetails => {
  const allFields = _.get(assessmentDetails, "allFields", []);
  const unitPlanAllFields = getUnitPlanFieldsFromAssessment({
    assessmentDetails,
  });

  const assessmentSubjectsNodes = _.get(
    _.find(allFields, { uid: "subjects" }),
    "resolvedMinimalTree.subjects",
    []
  );

  const unitSubjectNodes = _.get(
    _.find(unitPlanAllFields, { uid: "subjects" }),
    "resolvedMinimalTree.subjects",
    []
  );

  return _.map(
    _.uniqBy(
      [...(assessmentSubjectsNodes || []), ...(unitSubjectNodes || [])],
      "id"
    ),
    "name"
  );
};

export const getTableDataForMYPRubric = params => {
  const { doc, assessmentDetails, type } = params;

  const tableData = _.map(doc.querySelectorAll("table"), table => {
    const headers = _.map(table.querySelectorAll("th"), "textContent");
    const rows = _.map(table.querySelectorAll("tbody tr"), row => {
      return _.map(row.querySelectorAll("td"), "textContent");
    });

    return {
      headers,
      rows,
    };
  });

  const objectivesMYPNodes = getAssessmentAndUnitPlanCombinedNodes({
    assessmentDetails,
    type: MYP_OBJECTIVE_RUBRIC,
  });

  const interdisciplinaryCriteriaMYPNodes = getAssessmentAndUnitPlanCombinedNodes(
    {
      assessmentDetails,
      type: MYP_INTERDISCIPLINARY_CRITERIA_RUBRIC,
    }
  );

  const nodes = _.concat(objectivesMYPNodes, interdisciplinaryCriteriaMYPNodes);

  const depthTwoNodes = _.orderBy(
    _.filter(nodes, node => node?.depth === 2),
    "displaySequence"
  );

  let depthThreeNodes = [];

  _.forEach(depthTwoNodes, node => {
    const { children = [] } = node || {};

    _.forEach(children, child => {
      const depthThreeNode = _.find(nodes, node => {
        return node?.id === child;
      });

      if (!_.isEmpty(depthThreeNode)) {
        depthThreeNodes.push(depthThreeNode);
      }
    });
  });

  depthThreeNodes = _.orderBy(depthThreeNodes, "associatedParents.0.name");
  let index = 0;

  const result = [];

  _.forEach(tableData, item => {
    const { rows } = item || {};
    const mapping = {};

    const subject =
      _.get(depthThreeNodes[index], "associatedParents.0.name") ||
      MYP_INTERDISCIPLINARY_CRITERIA_RUBRIC;

    _.forEach(rows, (row, rowIndex) => {
      const originalCriteria = depthThreeNodes?.[index + rowIndex];
      const aiCriteria = keepTextAndNumbers(_.first(row));
      const { parent, label } = originalCriteria || {};

      const parentLabel = _.get(_.find(depthTwoNodes, { id: parent }), "label");

      const key = parentLabel ? `${parentLabel}_${aiCriteria}` : aiCriteria;

      /**
       key example -  "Objective D: Writing_use of grammatical structures"

       parentLabel - Objective D: Writing
       aiCriteria - use of grammatical structures
       originalCriteria - use a wide range of grammatical structures generally accurately   

       key = "Objective D: Writing_use of grammatical structures"
       value - "use a wide range of grammatical structures generally accurately"

       */
      mapping[subject] = {
        ...mapping[subject],
        [key]: keepTextAndNumbers(label),
      };
    });

    result.push({
      ...item,
      subject,
      mapping,
    });

    index += _.size(rows);
  });

  if (type === MYP_INTERDISCIPLINARY_CRITERIA_RUBRIC) {
    return _.filter(
      result,
      ({ subject }) => subject === MYP_INTERDISCIPLINARY_CRITERIA_RUBRIC
    );
  } else {
    return _.filter(
      result,
      ({ subject }) =>
        subject !== MYP_INTERDISCIPLINARY_CRITERIA_RUBRIC &&
        subject !== undefined &&
        subject !== "undefined"
    );
  }
};

/**
    Example  - 

    originalCriteria - use a wide range of grammatical structures generally accurately   
    aiGeneratedCriteria - use of grammatical structures
    subject - English - Lang Aq
    depthTwoNodeLabel - Objective D: Writing

    mypCriteriaMapping =  {
      "English - Lang Aq": {
        "Objective A: Listening_analyse conventions": "analyse conventions in a wide variety of simple authentic texts",
        "Objective B: Reading_analyse conventions": "analyse conventions in a wide variety of simple authentic texts",
        "Objective C: Speaking_use of grammatical structures": "use a wide range of grammatical structures generally accurately",
        "Objective D: Writing_use of grammatical structures": "use a wide range of grammatical structures generally accurately"
      }
    }
*/

export const compareMYPCriteriaNodeText = params => {
  const {
    originalCriteria,
    aiGeneratedCriteria,
    metadata,
    subject,
    depthTwoNodeLabel,
  } = params;

  const _originalCriteria = keepTextAndNumbers(originalCriteria);
  const _aiGeneratedCriteria = keepTextAndNumbers(aiGeneratedCriteria);

  const isMatch = _.includes(_originalCriteria, _aiGeneratedCriteria);

  if (!isMatch) {
    const { mypCriteriaMapping = {} } = metadata || {};

    return _.isEqual(
      _originalCriteria,
      mypCriteriaMapping?.[subject]?.[_aiGeneratedCriteria] || // for backward compatibility
        mypCriteriaMapping?.[subject]?.[
          `${depthTwoNodeLabel}_${_aiGeneratedCriteria}`
        ]
    );
  }

  return isMatch;
};

export const getShowImprovePrompts = params => {
  const { assessmentDetails } = params;

  const description = getFieldValue({
    fields: assessmentDetails?.allFields,
    uid: "description",
  });

  const trimmedValue = _.trim(htmlToText(description));
  return !_.isEmpty(trimmedValue) && trimmedValue !== "\n";
};

export const filterPrompts = params => {
  const { item, promptTypes = [], assessmentDetails } = params;

  const { fieldType, promptType, metadata } = item || {};
  const { actionConfiguration } = metadata || {};
  const { isUnitRequired = false } = actionConfiguration || {};

  const [group, subGroup] = _.split(fieldType, "_");

  const isUnitSelected = !_.isEmpty(
    getFieldValue({
      fields: assessmentDetails?.allFields,
      uid: "unitPlan",
    })
  );

  return (
    (!isUnitRequired || isUnitSelected) && // if unit is required and unit is not selected then filter the prompt
    (promptType === "user" ||
      (!_.isEmpty(promptTypes) && _.includes(promptTypes, promptType))) &&
    (group !== "IMPROVE" || getShowImprovePrompts(params))
  );
};

export const getFlattenedNodes = ({ nodes }) => {
  const flattenChildrendNodes = ({ node, nodes, flattenedNodes }) => {
    const { children } = node || {};

    flattenedNodes.push(node);

    if (!_.isEmpty(children)) {
      _.forEach(children, child => {
        const childNode = _.find(nodes, { id: child });
        if (!_.isEmpty(childNode)) {
          flattenChildrendNodes({
            node: childNode,
            nodes,
            flattenedNodes,
          });
        }
      });
    }
  };

  const flattenedNodes = [];

  const rootPrompts = _.filter(nodes, node => _.isEmpty(node?.parent));
  const nonRootPrompts = _.filter(nodes, node => !_.isEmpty(node?.parent));

  _.forEach(rootPrompts, node => {
    if (node) {
      flattenChildrendNodes({ node, nodes: nonRootPrompts, flattenedNodes });
    }
  });

  return flattenedNodes;
};

const assignDepthToPrompt = prompts => {
  const nodes = [...prompts];

  const calculateDepth = (nodes, nodeId) => {
    const node = _.find(nodes, n => n.id === nodeId);
    const { children } = node || {};

    if (!node) return 0;
    if (_.isEmpty(children)) return 1;

    const childrenDepths = _.map(children, childId =>
      calculateDepth(nodes, childId)
    );

    return 1 + Math.max(...childrenDepths);
  };

  _.forEach(nodes, node => {
    node.depth = calculateDepth(nodes, node.id);
  });

  return nodes;
};

const assignParentToPrompt = prompts => {
  const childToParentMap = {};

  _.forEach(prompts, prompt => {
    const { id, children } = prompt || {};

    if (!_.isEmpty(children)) {
      _.forEach(children, childId => {
        childToParentMap[childId] = id;
      });
    }
  });

  _.forEach(prompts, prompt => {
    prompt.parent = childToParentMap[prompt.id] || null;
  });

  return prompts;
};

const sortPrompts = prompts => {
  return _.orderBy(
    prompts,
    [
      prompt => {
        const { promptType, parent } = prompt || {};
        return promptType === "display" && _.isNil(parent) ? 0 : 1;
      },
      "displaySequence",
    ],
    ["asc", "asc"]
  );
};

const sortPromptsAndChildren = prompts => {
  const promptsWithSortedChildren = _.map(prompts, prompt => {
    const { children: childrenIds } = prompt || {};

    const children = sortPrompts(
      _.compact(_.map(childrenIds, id => _.find(prompts, { id })))
    );

    const updatedChildrenIds = _.map(children, "id");

    return {
      ...prompt,
      children: !_.isEmpty(updatedChildrenIds)
        ? updatedChildrenIds
        : childrenIds,
    };
  });

  return sortPrompts(promptsWithSortedChildren);
};

export const getPromptsForSuggestionLibrary = params => {
  const {
    t,
    prompts,
    language,
    searchText,
    assessmentDetails,
    unitPlanDetails,
    curriculumProgramType,
  } = params;

  // add label, isNewTagVisible and sort the prompts
  let updatedPrompts = getUpdatedPrompts({
    t,
    value: prompts,
    assessmentDetails,
    unitPlanDetails,
    isEnglishLanguageSelected: language === "en",
    curriculumProgramType,
    promptTypes: ["display", "user"],
  });

  // filter user prompts which are not children of any prompt and display prompts without children
  updatedPrompts = _.filter(updatedPrompts, prompt => {
    const { id, promptType, children } = prompt || {};
    if (promptType === "display") {
      return _.some(updatedPrompts, ({ id }) => _.includes(children, id));
    } else if (promptType === "user") {
      return _.some(updatedPrompts, ({ children } = {}) =>
        _.includes(children, id)
      );
    }

    return false;
  });

  // add depth to each prompt, leaf node will have depth = 1
  updatedPrompts = assignDepthToPrompt(updatedPrompts);

  // add parent to each prompt, root node will have parent = null
  updatedPrompts = assignParentToPrompt(updatedPrompts);

  // sort children of each prompt
  updatedPrompts = sortPromptsAndChildren(updatedPrompts);

  // handle search for the prompts which user can interact with
  const userPrompts = _.filter(updatedPrompts, prompt => {
    const { promptType, label } = prompt || {};

    if (promptType === "user") {
      const index = localSearch({
        text: _.toLower(label),
        searchText,
      });
      return index > -1 || _.isEmpty(searchText);
    }

    return false;
  });

  // displayPrompts are the headers or the prompts which are visible in the sidebar
  // also filter out improve prompts if description is empty
  let displayPrompts = _.filter(updatedPrompts, prompt => {
    const { promptType, fieldType, children } = prompt || {};

    return (
      promptType === "display" &&
      !_.isEmpty(children) &&
      (fieldType !== "IMPROVE" || getShowImprovePrompts({ assessmentDetails }))
    );
  });

  // as user prompts are filtered based on search, filter display prompts too (because they are the parent of user prompts)
  if (!_.isEmpty(searchText)) {
    const userPromptIds = _.map(userPrompts, "id");

    displayPrompts = _.reduce(
      _.orderBy(displayPrompts, "depth"),
      (acc, displayPrompt) => {
        const { depth, children } = displayPrompt;

        let filteredChildren = children;

        if (depth === 3) {
          filteredChildren = _.filter(children, child =>
            _.includes(_.map(acc, "id"), child)
          );
        } else {
          filteredChildren = _.filter(children, child =>
            _.includes(userPromptIds, child)
          );
        }

        if (!_.isEmpty(filteredChildren)) {
          acc.push({ ...displayPrompt, children: filteredChildren });
        }

        return sortPrompts(acc);
      },
      []
    );
  }

  const flattenedPrompts = getFlattenedNodes({
    nodes: [...displayPrompts, ...userPrompts],
  });

  return {
    flattenedPrompts,
  };
};

export const preserveAnchorTagFormatter = (elem, walk, builder) => {
  const href = elem.attribs.href || "";
  const target = elem.attribs.target || "";
  const rel = elem.attribs.rel || "";
  const dataAttachmentId = elem.attribs["data-attachment-id"] || "";
  const text = _.first(elem.children)?.data;

  const dataAttr = dataAttachmentId
    ? ` data-attachment-id="${dataAttachmentId}"`
    : "";

  builder.addInline(
    `<a href="${href}" target="${target}" rel="${rel}"${dataAttr}>${text}</a>`
  );
};

export const getContentToSend = params => {
  const {
    t,
    selectedAction,
    isEnglishLanguageSelected,
    query,
    isAITutor,
    conversationAttachments,
  } = params;

  let updatedQuery = query;

  // extra </span>. is added because the regex in 'replace func' below replaces string till the last closing span tag
  const replaceWith = !_.isEmpty(conversationAttachments)
    ? _.map(
        conversationAttachments,
        attachment =>
          `<span><a href="${attachment.url}" target="_blank" rel="noopener noreferrer" data-attachment-id="${attachment.id}">${attachment.name}</a></span>`
      ).join(", ") + "</span>."
    : "</span>.";

  // replaces <span> tag which has id="attachment-text" with anchor tag
  updatedQuery = _.replace(
    query,
    /<span\s+[^>]*id=["']attachment-text["'][^>]*>[\s\S]*<\/span>/gi,
    replaceWith
  );

  const htmlToTextConfig = {
    preserveNewlines: true,
    wordwrap: false,
    formatters: {
      anchorTagFormatter: preserveAnchorTagFormatter,
    },
    tags: {
      a: {
        format: "anchorTagFormatter",
      },
    },
  };

  const parsedQuery = _.trim(htmlToText(updatedQuery, htmlToTextConfig));

  if (!_.isEmpty(selectedAction)) {
    let content = t(selectedAction?.displayTextLocale);

    // 1. get the content which user has written in "Add addition instructions..." section
    let userInstructions = "",
      shouldAddSemiColon = false;

    if (isAITutor) {
      const parser = new DOMParser();
      const doc = parser.parseFromString(query, "text/html");
      userInstructions = doc.querySelector("#additional-instructions")
        ?.textContent;
    } else {
      const tempElement = document.createElement("div");
      tempElement.innerHTML = query;
      userInstructions = _.trim(_.get(tempElement, "lastChild.textContent"));

      shouldAddSemiColon = !_.isEmpty(userInstructions);
    }

    // 2. in AI tutor we can't set content = selectedAction?.displayText because user can modify the predefined prompt
    // also remove the additional instructions from the content, as we have already stored it in userInstructions
    if (isAITutor && parsedQuery) {
      if (!_.isEmpty(userInstructions)) {
        const tempElement = document.createElement("div");
        tempElement.innerHTML = updatedQuery;

        const userInstructionsElement = tempElement.querySelector(
          "#additional-instructions"
        );

        if (userInstructionsElement) userInstructionsElement.remove();

        content = tempElement.innerHTML;
      } else {
        content = _.cloneDeep(parsedQuery); // in AI tutor user can modify suggestion prompt
      }
    } else if (
      isEnglishLanguageSelected ||
      _.isNil(selectedAction?.displayTextLocale)
    ) {
      content = selectedAction?.displayText;
    }

    content = htmlToText(content, { wordwrap: null, ...htmlToTextConfig });

    return {
      content: shouldAddSemiColon ? `${_.trim(content)};` : _.trim(content),
      userInstructions,
    };
  } else {
    return {
      content: _.cloneDeep(parsedQuery),
      userInstructions: "",
    };
  }
};

export const isValidXML = xml => {
  const regex = /<tst>[\s\S]*?<\/tst>/;
  return regex.test(xml);
};

const pushChunk = params => {
  const {
    part,
    matches,
    result,
    type = "plaintext",
    isLocalised = true,
  } = params;

  const nameTagRegex = /<name>([\s\S]*?)<\/name>/g;

  if (_.size(matches) > 0) {
    const tutors = _.map(matches, match => {
      // <name>This is content of name tag</name>
      const nameTagContent = replaceXMLTags(_.first(match.match(nameTagRegex)));

      // Replace name tag content with bold text
      // <name>This is content of name tag</name> -> **This is content of name tag**
      const parsedText = replaceXMLTags(
        _.replace(match, nameTagContent, `**${nameTagContent}**`)
      )
        .split("\n")
        .map(line => _.trimStart(line))
        .join("\n");

      return {
        text: parsedText,
        type: "tutor",
        xml: match,
      };
    });

    result.push(...tutors);
  } else if (!_.isEmpty(part)) {
    result.push({ text: replaceXMLTags(part), type, isLocalised });
  }
};

export const getTutorChunks = inputText => {
  const isAIDebugModeEnabled =
    ACLStore.can("IdentityAccess:EnableAIDebugMode") ||
    ACLStore.can("Common:EnableAIDebugMode");

  const result = [];

  // Handle V2 enhancements: Hide internal tags and show loader while generating
  if (getIsAITutorEnhancementsV2Enabled()) {
    const loaderSensitiveTags = ["internal", "thinking", "commit_fields"];

    let cleanedText = inputText;

    const tagStates = _.map(loaderSensitiveTags, tagName => {
      const openRegex = new RegExp(`<${tagName}>`, "g");
      const closeRegex = new RegExp(`</${tagName}>`, "g");

      const openCount = (inputText.match(openRegex) || []).length;
      const closeCount = (inputText.match(closeRegex) || []).length;

      const completeTagRegex = new RegExp(
        `<${tagName}>([\\s\\S]*?)</${tagName}>`,
        "g"
      );
      const trailingTagRegex = new RegExp(`<${tagName}>[\\s\\S]*$`, "g");

      // For thinking and internal tags, completely remove content (don't preserve inner content)
      if (
        tagName === "thinking" ||
        tagName === "internal" ||
        tagName === "commit_fields"
      ) {
        cleanedText = cleanedText.replace(completeTagRegex, "");
        cleanedText = cleanedText.replace(trailingTagRegex, "");
      } else {
        // Preserve content while stripping complete tag wrappers for other tags
        cleanedText = cleanedText.replace(
          completeTagRegex,
          (match, innerContent = "") => innerContent
        );
        cleanedText = cleanedText.replace(trailingTagRegex, "");
      }

      return {
        tagName,
        hasAny: openCount > 0 || closeCount > 0,
        hasIncomplete: openCount > closeCount,
      };
    });

    // Render the cleaned content (without internal, commit_fields or thinking tags)
    if (!_.isEmpty(_.trim(cleanedText))) {
      pushChunk({
        part: _.trim(cleanedText),
        result,
        type: isAIDebugModeEnabled ? "plaintext" : "plaintext_without_actions",
      });
    }

    const hasIncompleteTag = _.some(tagStates, "hasIncomplete");
    const hasAnyTag = _.some(tagStates, "hasAny");

    const internalTagState = _.find(tagStates, { tagName: "internal" }) || {};
    const commitFieldsTagState =
      _.find(tagStates, { tagName: "commit_fields" }) || {};

    const shouldShowLoaderForTags =
      hasIncompleteTag || (hasAnyTag && _.isEmpty(_.trim(cleanedText)));

    const loaderPart =
      internalTagState.hasIncomplete || commitFieldsTagState.hasIncomplete
        ? "ai:configuring_template_fields"
        : "ai:thinking";

    // Show loader if conditions require it or if minimum duration hasn't passed
    if (shouldShowLoaderForTags) {
      pushChunk({
        part: loaderPart,
        result,
        type: "loader",
        isLocalised: false,
      });
    } else {
      // remove loader once complete tag is generated
      _.remove(result, { type: "loader" });
    }

    return result;
  }

  const tutorTagRegex = /<Tutor>([\s\S]*?)(?=<\/Tutor>|$)/g;
  const tdtTagRegex = /<tdt>([\s\S]*?)(?=<\/tdt>|$)/g;

  const tstTagRegex = /<tst>([\s\S]*?)(?=<\/tst>|$)/g; // will return true when just <tst> is encountered
  const tstValidTagRegex = /<tst>([\s\S]*?)<\/tst>/; // will return true only when valid <tst>..</tst> is encountered

  const lngTagRegex = /<lng>([\s\S]*?)(?=<\/lng>|$)/g; // will return true when just <lng> is encountered
  const lngTagValidTagRegex = /<lng>([\s\S]*?)<\/lng>/; // will return true only when valid <lng>..</lng> is encountered

  const tutorMatch = inputText.match(tutorTagRegex) || [];
  const tdtMatch = inputText.match(tdtTagRegex) || [];

  const parts = inputText.split("----");

  if (!_.isEmpty(tutorMatch)) {
    for (let i = 0; i < _.size(parts); i++) {
      const part = _.trim(parts[i]);
      const matches = part.match(tutorTagRegex) || [];
      pushChunk({ part, matches, result });
    }
    // if <tdt> tag is present in the text
  } else if (!_.isEmpty(tdtMatch)) {
    // split to welcome message, tutor details (title, description, opening message)
    // analysis scale, tutor role and rules, ending message
    const sections = _.chain(inputText)
      .split(/###|<\/?tdt>|<\/?lng>|<\/?anls>|<\/?tst>/)
      .map(_.trim)
      .filter(section => section !== "")
      .value();

    _.forEach(sections, section => {
      //  will match with incomplete tst and lng tag
      const settingsSection = _.first(inputText.match(tstTagRegex));
      const learningGoalsSection = _.first(inputText.match(lngTagRegex));

      //  will match only when complete tst and lng tag is generated
      const completeSettingsSection = _.first(
        inputText.match(tstValidTagRegex)
      );
      const completeLearningGoalsSection = _.first(
        inputText.match(lngTagValidTagRegex)
      );

      const isAITutorSettingsSection = _.isEqual(
        section,
        replaceXMLTags(settingsSection)
      );
      const isLearningGoalsSection = _.isEqual(
        section,
        replaceXMLTags(learningGoalsSection)
      );

      if (isAITutorSettingsSection) {
        // if complete tst tag is not generated then show loader
        if (!isValidXML(completeSettingsSection)) {
          pushChunk({
            part: "ai:defining_tutor_role_and_rules_msg",
            result,
            type: "loader",
            isLocalised: false,
          });
        } else {
          // remove loader once complete tag is generated
          _.remove(result, { type: "loader" });
        }
      } else if (isLearningGoalsSection) {
        if (!isValidXML(completeLearningGoalsSection)) {
          pushChunk({
            part: "ai:thinking_of_learning_goals",
            result,
            type: "loader",
            isLocalised: false,
          });
        } else {
          // remove loader once complete tag is generated
          _.remove(result, { type: "loader" });
        }
      } else {
        pushChunk({
          part: _.replace(section, "----", ""),
          result,
          type: isAIDebugModeEnabled
            ? "plaintext"
            : "plaintext_without_actions",
        });

        _.remove(result, { type: "loader" });
      }
    });
  } else {
    pushChunk({ part: _.trim(inputText), matches: tutorMatch, result });
  }

  if (_.some(result, { type: "tutor" })) {
    return _.map(result, item => {
      if (item.type === "plaintext") {
        return {
          ...item,
          type: "plaintext_without_actions",
        };
      }

      return item;
    });
  }

  return result;
};

export const convertXMLToObj = xmlString => {
  const nameRegex = /<name>\s*([\s\S]*?)\s*<\/name>/;
  const summaryRegex = /<summary>\s*([\s\S]*?)\s*<\/summary>/;

  const nameMatch = xmlString.match(nameRegex);
  const summaryMatch = xmlString.match(summaryRegex);

  return {
    name: nameMatch ? nameMatch[1] : "",
    summary: summaryMatch ? summaryMatch[1] : "",
  };
};

export const getAIPromptFilters = params => {
  const {
    curriculumProgramType,
    assessmentDetails,
    unitPlanDetails,
    isAITutor = false,
  } = params;
  const promptFilters = {};
  const assessmentSubjects = getResolvedMinimalTree({
    fields: _.get(assessmentDetails, "allFields"),
    uid: "subjects",
  });

  let subjects = _.get(assessmentSubjects, "subjects", []);
  let subjectGroups = _.map(subjects, "subjectGroup");

  if (_.isEmpty(subjects)) {
    const unitPlanSubjects = getResolvedMinimalTree({
      fields: _.get(unitPlanDetails, "unitPlanFields"),
      uid: "subjects",
    });

    if (!_.isEmpty(unitPlanSubjects)) {
      subjects = _.get(unitPlanSubjects, "subjects", []);
      subjectGroups = _.map(subjects, "subjectGroup");
    }
  }
  const unitType = getFieldValue({
    fields: _.get(unitPlanDetails, "unitPlanFields"),
    uid: "unitType",
  });
  const subjectIds = _.map(subjects, "id");
  const subjectGroupIds = _.map(subjectGroups, "id");

  switch (curriculumProgramType) {
    case CURRICULUM_TYPE_PYP:
    case CURRICULUM_TYPE_UBD:
      {
        if (!_.isEmpty(subjectIds)) {
          _.set(promptFilters, "categories", [
            ..._.get(promptFilters, "categories", []),
            {
              entityType: "SUBJECT",
              entities: subjectIds,
            },
          ]);
        }
      }
      break;
    case CURRICULUM_TYPE_MYP:
      {
        if (!_.isEmpty(subjectGroupIds)) {
          _.set(promptFilters, "subjectGroupIds", subjectGroupIds);
        }
      }
      break;
    default: {
      if (!_.isEmpty(subjectIds)) {
        _.set(promptFilters, "subjectIds", subjectIds);
      }

      if (!_.isEmpty(subjectGroupIds)) {
        _.set(promptFilters, "subjectGroupIds", subjectGroupIds);
      }
    }
  }

  if (isAITutor) {
    const tutorCategories = [];

    if (!getIsAITutorModalitiesEnabled()) {
      tutorCategories.push({
        entityType: "CUSTOM",
        entities: "TOP_SIX",
      });
    }

    if (_.isEmpty(promptFilters)) {
      tutorCategories.push({
        entityType: "CUSTOM",
        entities: "GENERIC",
      });
    }

    _.set(promptFilters, "categories", [
      ..._.get(promptFilters, "categories", []),
      ...tutorCategories,
    ]);

    _.set(promptFilters, "entityRelationType", "AND");
  } else {
    if (!_.isEmpty(unitType)) {
      _.set(promptFilters, "categories", [
        ..._.get(promptFilters, "categories", []),
        {
          entityType: "UNIT_TYPE",
          entities: [unitType],
        },
      ]);
    } else if (
      _.size(subjects) > 1 &&
      INTERDISCIPLINARY_UNIT_TYPES[curriculumProgramType]
    ) {
      _.set(promptFilters, "categories", [
        ..._.get(promptFilters, "categories", []),
        {
          entityType: "UNIT_TYPE",
          entities: [INTERDISCIPLINARY_UNIT_TYPES[curriculumProgramType]],
        },
      ]);
    }
  }

  return promptFilters;
};

export const getAITutorSidebarOptions = params => {
  const { t, i18n, displayPrompts } = params;

  const root = {
    key: "root",
    children: [],
  };

  const childOptions = _.reduce(
    _.orderBy(displayPrompts, ["displaySequence"]),
    (result, prompt) => {
      const { id, label, parent, action, children = [] } = prompt;

      const { displayText } = getPromptLabelsObj({
        t,
        i18n,
        action,
        displayText: label,
      });

      if (!parent) {
        root.children.push(id);
        result.push({
          key: id,
          title: <span style={styles.sidebarLabelStyle}>{displayText}</span>,
          tier: 1,
          parent: "root",
          children,
        });
      }

      return result;
    },
    []
  );

  return [root, ...childOptions];
};

export const getAITutorTopSixPrompts = params => {
  const { prompts } = params;

  let updatedPrompts = _.cloneDeep(prompts);
  updatedPrompts = assignDepthToPrompt(updatedPrompts);
  updatedPrompts = sortPromptsAndChildren(updatedPrompts);

  const genericPrompts = _.find(prompts, {
    action: "generic_tutor",
  });

  if (!_.isEmpty(genericPrompts)) {
    const filteredPrompts = _.filter(updatedPrompts, {
      promptType: "display",
      depth: 2,
    });

    if (!_.isEmpty(filteredPrompts)) updatedPrompts = filteredPrompts;

    const genericPromptChildrenPrompts = _.filter(updatedPrompts, prompt => {
      return _.includes(genericPrompts?.children, prompt.id);
    });

    if (!_.isEmpty(genericPromptChildrenPrompts)) {
      updatedPrompts = genericPromptChildrenPrompts;
    }
  } else {
    const filteredPrompts = _.filter(updatedPrompts, {
      promptType: "display",
      depth: 2,
    });

    if (!_.isEmpty(filteredPrompts)) updatedPrompts = filteredPrompts;
  }

  return _.chain(updatedPrompts).sortBy("displaySequence").take(6).value();
};

export const convertAIResponseToToolStructure = params => {
  const { metadata, content = {}, tool, assessmentDetails } = params || {};

  const field = metadata?.fieldType;

  try {
    switch (field) {
      case RUBRIC: {
        const rubric = {};

        rubric["indicators"] = _.get(content, "criterias", []);
        rubric["criterias"] = _.get(content, "descriptorLevels", []);
        rubric["descriptors"] = _.map(
          _.get(content, "descriptors", []),
          descriptor => {
            const {
              label,
              criteriaId: indicatorId,
              descriptorLevelId: criteriaId,
            } = descriptor || {};

            return {
              id: generateRandomId(),
              label,
              criteria: {
                id: criteriaId,
              },
              indicator: {
                id: indicatorId,
              },
            };
          }
        );

        return rubric;
      }
      case CHECKLIST: {
        return {
          checklistOptionItems: _.get(content, "checklistOptions", []),
          checklistItems: _.get(content, "criterias", []),
        };
      }

      case SINGLE_POINT_RUBRIC: {
        return {
          criterias: _.get(content, "criterias", []),
          standards: _.map(
            _.get(content, "standards", []),
            (standard, index) => {
              return {
                ...standard,
                id: index,
              };
            }
          ),
        };
      }
      case MYP_OBJECTIVE_RUBRIC:
      case MYP_INTERDISCIPLINARY_CRITERIA_RUBRIC: {
        const descriptors = _.get(tool, "descriptors", []);
        const descriptorsGroup = _.groupBy(descriptors, "objective.id");

        const nodes = getAssessmentAndUnitPlanCombinedNodes({
          assessmentDetails,
          type: field,
        });

        const result = [];

        /**
         * example of content =>
         * [[6],[6],[6],[6]]
         * [[6],[6]]
         * [[6],[6],[6]]
         * [[6]]
         *
         *
         * [[6],[6],[6],[6]] -> represent number of criteria selected for a objective (A, B, C, D)
         * [6] -> represents each row (criteria) of the table
         * 6 -> represents each cell (descriptor) of the table
         */

        const objectiveIds = [];

        _.forEach(content, rows => {
          _.forEach(rows, row => {
            // achievementLevel will be empty for the first cell of each row
            // it will help us to identify the objective id

            if (_.isEmpty(_.first(row)?.achievementLevel)) {
              const objectiveId = _.find(descriptors, descriptor => {
                const depthTwoNodeLabel = _.get(
                  _.find(nodes, {
                    id: _.get(descriptor, "objective.parent"),
                    depth: 2,
                  }),
                  "label"
                );

                return (
                  !_.includes(objectiveIds, descriptor?.objective?.id) &&
                  compareMYPCriteriaNodeText({
                    originalCriteria: _.get(descriptor, "objective.label"),
                    aiGeneratedCriteria: _.first(row)?.label,
                    metadata,
                    subject: _.first(row)?.subject,
                    depthTwoNodeLabel,
                  })
                );
              })?.objective?.id;

              if (objectiveId) objectiveIds.push(objectiveId);
            }

            const objectiveId = _.last(objectiveIds);

            // _.tail will remove the first cell of each row (where achievementLevel is empty)
            const rowTail = _.tail(row);

            _.forEach(_.get(descriptorsGroup, objectiveId, []), descriptor => {
              const currentDescriptor = _.find(rowTail, {
                achievementLevel: descriptor?.achievementLevel?.label,
              });
              result.push({
                id: descriptor?.id,
                label: currentDescriptor?.label,
              });
            });
          });
        });

        return result;
      }
      case ANALYSIS: {
        const analysisScale = {};
        analysisScale["criterias"] = _.get(content, "firstColumnData", []);
        analysisScale["description"] = _.get(content, "secondColumnData", []);

        return analysisScale;
      }
      default:
        return content;
    }
  } catch (e) {
    return null;
  }
};

export const isPromptEqual = (
  arr1,
  arr2,
  keys = ["field", "type", "isRequired"]
) => {
  const transform = (array, keys) => _.map(array, item => _.pick(item, keys));

  const transformedArr1 = transform(arr1, keys);
  const transformedArr2 = transform(arr2, keys);
  return _.isEqual(_.sortBy(transformedArr1), _.sortBy(transformedArr2));
};

export const getActionsList = params => {
  const { message } = params;

  const actions = [];

  if (!_.isEmpty(message?.content)) {
    actions.push({
      key: "copy",
      icon: <CopyOutlined />,
      tooltipLabel: "common:copy",
    });
  }

  return actions;
};

/**
 * Removes thinking, commit_fields and internal tags along with their content from a string
 * @param {string} content - Content that may contain thinking/internal/commit_fields tags
 * @returns {string} Content with thinking/internal/commit_fields tags removed
 */
export const removeThinkingAndInternalTags = content => {
  if (!content) return content;

  let cleanedContent = content;

  // Remove complete thinking tags with their content
  cleanedContent = _.replace(
    cleanedContent,
    /<thinking>[\s\S]*?<\/thinking>/g,
    ""
  );

  // Remove complete internal tags with their content
  cleanedContent = _.replace(
    cleanedContent,
    /<internal>[\s\S]*?<\/internal>/g,
    ""
  );

  // Remove complete commit_fields tags with their content
  cleanedContent = _.replace(
    cleanedContent,
    /<commit_fields>[\s\S]*?<\/commit_fields>/g,
    ""
  );

  // Remove incomplete thinking tags (opened but not closed)
  cleanedContent = _.replace(cleanedContent, /<thinking>[\s\S]*$/g, "");

  // Remove partial <thinking tag opening at the end (e.g. <th)
  cleanedContent = cleanedContent.replace(/<(?:th|thinking)[^>]*$/, "");

  // Remove incomplete internal tags (opened but not closed)
  cleanedContent = _.replace(cleanedContent, /<internal>[\s\S]*$/g, "");

  // Remove incomplete commit_fields tags (opened but not closed)
  cleanedContent = _.replace(cleanedContent, /<commit_fields>[\s\S]*$/g, "");

  // Remove standalone < at the end if it's likely the start of a thinking/internal tag
  if (_.endsWith(cleanedContent, "<")) {
    cleanedContent = cleanedContent.slice(0, -1);
  }

  return cleanedContent;
};

/**
 * Checks if the AI is in thinking state (generating thinking/internal tags)
 * @param {Array} messages - Array of message nodes
 * @returns {boolean} True if in thinking state (only thinking/internal tags present), false otherwise
 */
export const checkIsThinkingState = messages => {
  if (_.isEmpty(messages)) {
    return false;
  }

  const lastMessageContent = _.get(_.last(messages), "node.content");
  if (!lastMessageContent) return false;

  // Check if content has thinking or internal tags or incomplete tags
  const incompleteTagRegex = /<$/g;
  const thinkingOpenTagRegex = /<thinking>/g;
  const thinkingCloseTagRegex = /<\/thinking>/g;
  const internalOpenTagRegex = /<internal>/g;
  const internalCloseTagRegex = /<\/internal>/g;
  const commitFieldsOpenTagRegex = /<commit_fields>/g;
  const commitFieldsCloseTagRegex = /<\/commit_fields>/g;

  const hasThinkingOpenTag = thinkingOpenTagRegex.test(lastMessageContent);
  const hasThinkingCloseTag = thinkingCloseTagRegex.test(lastMessageContent);
  const hasInternalOpenTag = internalOpenTagRegex.test(lastMessageContent);
  const hasInternalCloseTag = internalCloseTagRegex.test(lastMessageContent);
  const hasCommitFieldsOpenTag = commitFieldsOpenTagRegex.test(
    lastMessageContent
  );
  const hasCommitFieldsCloseTag = commitFieldsCloseTagRegex.test(
    lastMessageContent
  );

  const hasAnyThinkingOrInternalTag =
    hasThinkingOpenTag ||
    hasThinkingCloseTag ||
    hasInternalOpenTag ||
    hasInternalCloseTag ||
    incompleteTagRegex ||
    hasCommitFieldsOpenTag ||
    hasCommitFieldsCloseTag;

  // If no thinking/internal/commit_fields tags present, not in thinking state
  if (!hasAnyThinkingOrInternalTag) return false;

  // Remove thinking,commit_fields  and internal tags with their content
  const contentWithoutTags = removeThinkingAndInternalTags(lastMessageContent);

  // If there's actual content outside thinking/internal/commit_fields tags, not in thinking state
  const hasActualContent = !_.isEmpty(_.trim(contentWithoutTags));

  // Return true only if thinking/internal/commit_fields tags exist but no actual content
  return !hasActualContent;
};

export const customContentParser = content => {
  // Remove thinking and internal tags with their content
  let updatedContent = removeThinkingAndInternalTags(content);

  // Apply other transformations
  updatedContent = _.replace(updatedContent, /###/g, "\n\n###\n\n");
  updatedContent = _.replace(updatedContent, /###/g, "----");
  return updatedContent;
};

const getHierarchyOfField = ({ fieldUID, templateBody }) => {
  if (!fieldUID || !templateBody) return {};

  const hierarchy = {};
  const { step_list: stepList = [] } = templateBody;

  const stepUid = _.findKey(stepList, ({ fields = [] }) =>
    _.includes(fields, fieldUID)
  );

  if (!stepUid) return hierarchy;
  hierarchy.step = stepUid;

  return hierarchy;
};

export const getFieldLocaleMapping = ({ t, templateBody }) => {
  const fieldList = _.get(templateBody, "field_list", []);
  const stepList = _.get(templateBody, "step_list", []);

  const _fieldLocaleMapping = {};

  _.forEach(fieldList, (field, fieldUid) => {
    const label = _.get(field, "config.label", "");
    _fieldLocaleMapping[fieldUid] = label;

    if (_.isEmpty(label)) {
      if (fieldUid === "title") {
        _fieldLocaleMapping[fieldUid] = t("common:title");
      }

      // take label from step when not present in field config
      const { step: stepId } = getHierarchyOfField({
        fieldUID: fieldUid,
        templateBody,
      });

      const stepObj = _.get(stepList, stepId, {});
      const stepLabel = _.get(stepObj, "label", "");

      if (!_.isEmpty(stepLabel)) {
        _fieldLocaleMapping[fieldUid] = stepLabel;
      }
    }
  });

  return _.pickBy(_fieldLocaleMapping, value => !_.isEmpty(value));
};

export const safeToolArgumentsJSONParse = args => {
  try {
    const parsedArgs = JSON.parse(args);
    return parsedArgs;
  } catch {
    return args;
  }
};
